import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Medal, Trophy } from "lucide-react";
import { API } from "../App";

const rankStyles = {
  1: {
    border: "border-yellow-400",
    bg: "bg-gradient-to-br from-yellow-100 to-yellow-50",
    medal: "text-yellow-500",
    label: "1st",
  },
  2: {
    border: "border-slate-300",
    bg: "bg-gradient-to-br from-slate-100 to-white",
    medal: "text-slate-400",
    label: "2nd",
  },
  3: {
    border: "border-amber-500",
    bg: "bg-gradient-to-br from-amber-100 to-amber-50",
    medal: "text-amber-600",
    label: "3rd",
  },
};

/**
 * Homepage widget showing the top 3 Prediction League placings for the
 * current season. Backed by a Monday-06:00-UK snapshot in Mongo, so the
 * podium doesn't shift mid-week.
 */
export function TopPredictorsWidget() {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/score-predictor/top-3`);
        if (!cancelled) setSnapshot(res.data);
      } catch (e) {
        // Silent — widget hides itself when empty
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return null;
  const top3 = snapshot?.top3 || [];
  if (top3.length === 0) return null;

  const generated = snapshot?.generated_at
    ? new Date(snapshot.generated_at).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "";

  return (
    <div className="mt-6" data-testid="top-predictors-widget">
      <Link to="/score-predictor" className="block group">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
            <div className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-[#7C2C3B]" />
              <h3
                className="text-2xl font-bold text-[#0F172A]"
                style={{ fontFamily: "Barlow Condensed" }}
              >
                TOP PREDICTORS — {snapshot?.season}
              </h3>
            </div>
            <span className="text-[11px] text-slate-500">
              Snapshot · {generated} · updates Mondays 06:00 UK
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {top3.map((entry) => {
              const style = rankStyles[entry.rank] || rankStyles[3];
              return (
                <div
                  key={entry.user_id}
                  className={`rounded-xl border-2 ${style.border} ${style.bg} p-4 flex items-center gap-3`}
                  data-testid={`top-predictor-rank-${entry.rank}`}
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-white border border-slate-200 flex items-center justify-center">
                    <Medal className={`w-6 h-6 ${style.medal}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                      {style.label}
                    </p>
                    <p className="text-base font-bold text-slate-900 truncate">
                      {entry.user_name}
                    </p>
                    <p className="text-xs text-slate-600">
                      <span className="font-bold text-[#7C2C3B]">
                        {entry.total_points}
                      </span>{" "}
                      pts · {entry.predictions_made} preds
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <p className="text-xs text-slate-500 mt-4 text-right group-hover:text-[#7C2C3B] transition-colors">
            See the full leaderboard →
          </p>
        </div>
      </Link>
    </div>
  );
}

export default TopPredictorsWidget;
